import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { tokens } from "../theme";
import type {
  SignageAssignment,
  SignageAssignmentPatch,
  SignagePlaylist,
  SignageScreenGroup,
} from "@shared/types/signage";

/** The third state, and the reason this dialog is not two dropdowns. */
const INHERIT = "__inherit__";

/**
 * What a group plays.
 *
 * ## Why every field offers "inherit"
 *
 * A group is a LAYER, and a layer's most important answer is often "I have no
 * opinion about this". A group made to put one channel on the treadmill screens
 * must not also decide their sound - fifty walls would silently take a setting
 * nobody chose, and the location's own answer would stop being reachable.
 *
 * So each control has three states, not two: inherit, and the two real values.
 * "Inherit" is the default for a group that has never been given an opinion,
 * and choosing it again RETRACTS one - which the wire expresses as `null`,
 * because a boolean cannot say "nothing to say" on its own.
 *
 * ## Why this is not the full assignment editor
 *
 * A screen's editor offers a weekly plan, campaigns, break rates and
 * orientation. Those are either physical facts about one wall (orientation) or
 * a week somebody builds screen by screen. What a GROUP is for is the two
 * settings you would otherwise change fifty times: what plays by default, and
 * whether it makes noise.
 */
export default function SignageGroupContentDialog({
  group,
  current,
  playlists,
  saving,
  error,
  onClose,
  onSave,
}: {
  /** The group whose content is being set, or null when closed. */
  group: SignageScreenGroup | null;
  /** What the group already says, or null if it has never said anything. */
  current: SignageAssignment | null;
  playlists: SignagePlaylist[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (patch: SignageAssignmentPatch) => void;
}) {
  const [playlistId, setPlaylistId] = useState(INHERIT);
  const [sound, setSound] = useState(INHERIT);

  useEffect(() => {
    if (!group) return;
    setPlaylistId(current?.defaultPlaylistId || INHERIT);
    // `undefined` and `null` both mean the group never said - only a real
    // boolean is an opinion.
    setSound(
      current?.soundEnabled === true
        ? "on"
        : current?.soundEnabled === false
          ? "off"
          : INHERIT
    );
  }, [group, current]);

  const screens = group?.screenIds.length ?? 0;

  return (
    <Dialog open={group !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "10px",
              bgcolor: tokens.brandTintSoft,
              display: "grid",
              placeItems: "center",
              color: tokens.brand,
            }}
          >
            <PlaylistPlayIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              What “{group?.name}” plays
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              {screens} {screens === 1 ? "screen" : "screens"} follow this
              group.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap={2.5}>
          <TextField
            select
            fullWidth
            size="small"
            label="Default channel"
            value={playlistId}
            onChange={(e) => setPlaylistId(e.target.value)}
            inputProps={{ "aria-label": "Default channel" }}
            helperText="What these screens play when nothing more specific is scheduled."
          >
            <MenuItem value={INHERIT}>
              Inherit — the group says nothing
            </MenuItem>
            {playlists.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Sound"
            value={sound}
            onChange={(e) => setSound(e.target.value)}
            inputProps={{ "aria-label": "Sound" }}
            helperText="“Off” is an instruction, not an absence — it silences every screen in the group."
          >
            <MenuItem value={INHERIT}>
              Inherit — the group says nothing
            </MenuItem>
            <MenuItem value="on">On</MenuItem>
            <MenuItem value="off">Off</MenuItem>
          </TextField>

          {/*
            The rule an operator will otherwise discover by being surprised: a
            screen that was set directly keeps winning, and this dialog cannot
            override it. Said here rather than after the fact.
          */}
          <Alert severity="info" sx={{ fontSize: 13 }}>
            A screen with its own setting keeps it — a group is asked only where
            the screen itself has no answer. Between two groups, the higher
            priority wins.
          </Alert>
        </Stack>
      </DialogContent>

      {error && (
        <Typography sx={{ px: 3, pt: 1, fontSize: 13, color: tokens.error }}>
          {error}
        </Typography>
      )}

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={saving}
          onClick={() =>
            onSave({
              // "" is how this wire retracts a playlist: the store writes null,
              // and the resolver skips null exactly as it skips absent.
              defaultPlaylistId: playlistId === INHERIT ? "" : playlistId,
              // `null` is the retraction. `false` would be an instruction to be
              // silent, which is a different and much louder thing.
              soundEnabled:
                sound === INHERIT ? null : sound === "on" ? true : false,
            })
          }
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
